/** Sidebar / route page URL for Roles; BE grants it through /my pagePermissions. */
export const ROLES_PAGE_URL = 'roles/list';

/** /my actionPermissions module behind the Roles page (devapi sends Create and Update). */
export const ROLE_PERMISSION_MODULE = 'Role';
export const RoleAction = {
  Create: 'Create',
  Update: 'Update',
} as const;

/** One row of the Roles list (Figma 01). */
export interface RoleListItem {
  id: string;
  name: string;
  description: string;
  userCount: number;
  /** Modules where the role has at least one action allowed, in display order. */
  modules: string[];
}

export interface RoleSearchRequest {
  page: number;
  pageSize: number;
  keyword?: string;
}

/** One permission checkbox (an /my actionPermissions `{module, action}` pair). */
export interface PermissionAction {
  action: string;
  label: string;
  description?: string;
  isAllowed: boolean;
  /** Another action of the same group this one needs (the group's "View"); hidden while that one is off. */
  dependsOn?: string;
}

/** One collapsible block of the permission matrix (Figma 02). */
export interface PermissionGroup {
  module: string;
  label: string;
  order: number;
  actions: PermissionAction[];
}

export interface RoleDetail {
  id: string;
  name: string;
  description: string;
  /** False for roles the BE never lets anyone edit. */
  isEditable: boolean;
  permissionGroups: PermissionGroup[];
}

export interface RolePermissionChange {
  module: string;
  action: string;
  isAllowed: boolean;
}

export interface UpdateRolePermissionsRequest {
  actions: RolePermissionChange[];
}

/** Users tab (Figma 05): a user from /v1/user/search, with the companies and locations of this role only. */
export interface RoleUser {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  profilePictureWebUrl?: string | null;
  companies: string[];
  locations: string[];
  createdOn: string | Date;
}

export interface RoleUserSearchRequest {
  page: number;
  pageSize: number;
  keyword?: string;
  /** Company (subsidiary) ids. */
  companyIds?: string[];
  /** Location (store) ids. */
  storeIds?: string[];
}

/** Company / Locations filter option. */
export interface RoleFilterOption {
  id: string;
  name: string;
}

// ── /v1/RolePermission API shapes ──────────────────────────────────────────

/** POST /v1/RolePermission/roles/search item. */
export interface ApiRolePermissionRoleListItem {
  roleId: string;
  name?: string | null;
  users: number;
  moduleAccess?: { key?: string | null; name?: string | null }[] | null;
}

/** One checkbox of GET /v1/RolePermission/{roleId}. */
export interface ApiRolePermissionItem {
  key?: string | null;
  name?: string | null;
  isAllowed: boolean;
}

/** One section (module block) of GET /v1/RolePermission/{roleId}. */
export interface ApiRolePermissionSection {
  key?: string | null;
  name?: string | null;
  module: string;
  isAllowed: boolean;
  items?: ApiRolePermissionItem[] | null;
}

/** GET / PUT /v1/RolePermission/{roleId} response. */
export interface ApiRolePermissionMatrix {
  roleId: string;
  roleName?: string | null;
  sections?: ApiRolePermissionSection[] | null;
}

/** PUT /v1/RolePermission/{roleId} body; only the changed checkboxes need to be sent. */
export interface ApiRolePermissionUpdateRequest {
  items: { key: string; isAllowed: boolean }[];
}

/** The listing page a role was opened from, for "2 of 12 in All Roles" and the up/down arrows. */
export interface RoleListContext {
  ids: string[];
  request: RoleSearchRequest;
  totalRecords: number;
}
